'use client'

import React, { useId, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { AccordionBlock as AccordionBlockType, Media } from '@/payload-types'
import './accordion.scss'

export const AccordionBlock: React.FC<AccordionBlockType> = ({
  color = 'primary',
  headingTag = 'h2',
  accordionWidth = 'col-lg-8',
  image,
  layout = 'accordionImage',
  noImageFilter = false,
  filterOpacity = '0.65',
  firstItemOpen = true,
  items,
}) => {
  const [openIndex, setOpenIndex] = useState<number>(firstItemOpen ? 0 : -1)
  const reactId = useId()
  const accordionId = `accordion-${reactId.replace(/:/g, '')}`

  // Respect prefers-reduced-motion: collapse the timing rather than dropping the cue.
  // Easing mirrors $cubiTr (cubic-bezier(0.2, 0.6, 0.2, 1)) used across the design system.
  const shouldReduceMotion = useReducedMotion()
  const collapseTransition = shouldReduceMotion
    ? { duration: 0 }
    : { duration: 0.3, ease: [0.2, 0.6, 0.2, 1] as [number, number, number, number] }

  if (!items || items.length === 0) {
    return null
  }

  // Convert color to PascalCase for CSS class
  const colorValue = color || 'primary'
  const colorClass = `color${colorValue.charAt(0).toUpperCase() + colorValue.slice(1)}`

  // Dynamic heading tag
  const HeadingTag = headingTag as React.ElementType

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index)
  }

  // Check if we have an image
  const hasImage = accordionWidth !== 'col-lg-12'

  // Calculate image column class
  const getImageColumnClass = () => {
    if (accordionWidth === 'col-lg-6') return 'col-lg-6'
    if (accordionWidth === 'col-lg-8') return 'col-lg-4'
    if (accordionWidth === 'col-lg-9') return 'col-lg-3'
    return ''
  }

  const imageColumnClass = getImageColumnClass()

  // Get image URL
  const imageUrl = typeof image === 'object' && image !== null ? (image as Media).url : ''

  // Filter classes
  const filterClass = noImageFilter ? '' : colorClass
  const opacityClass = noImageFilter ? '' : `opacity-${(filterOpacity || '0.8').replace('.', '-')}`

  // Accordion content
  const accordionContent = (
    <div className={`col-12 ${accordionWidth}`}>
      <div className={`accordion ${colorClass}`} id={accordionId}>
        {items.map((item, index) => {
          const isOpen = openIndex === index
          const itemId = `${accordionId}-item-${index}`

          return (
            <div key={index} className="accordion-item">
              <HeadingTag className="accordion-header">
                <button
                  className={`accordion-button ${!isOpen ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => toggleItem(index)}
                  aria-expanded={isOpen}
                  aria-controls={itemId}
                >
                  <span>{item.title}</span>
                  <i className="icon icon-chevron-down-3"></i>
                </button>
              </HeadingTag>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="content"
                    id={itemId}
                    role="region"
                    className="accordion-collapse"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={collapseTransition}
                    style={{ overflow: 'hidden' }}
                  >
                    <div className="accordion-body">{item.content}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </div>
  )

  // Image content
  const imageContent = hasImage ? (
    <div className={`col-12 ${imageColumnClass}`}>
      <div
        className={`accordionImage ${filterClass} ${opacityClass}`}
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
    </div>
  ) : null

  // If no image, just render accordion
  if (!hasImage) {
    return accordionContent
  }

  // If image, render in layout order
  return (
    <div className="accordionContainer">
      <div className="row g-4">
        {layout === 'accordionImage' ? (
          <>
            {accordionContent}
            {imageContent}
          </>
        ) : (
          <>
            {imageContent}
            {accordionContent}
          </>
        )}
      </div>
    </div>
  )
}
